import { useEffect, useState, useCallback } from 'react'
import { apiFetch } from '@/lib/api'

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7) // 7h → 19h

interface Slot {
  id: string
  startAt: string
  endAt: string
}

interface RecurringRule {
  id: string
  dayOfWeek: number
  startH: number
  startM: number
  endH: number
  endM: number
  label: string | null
  isActive: boolean
}

function pad(n: number) { return String(n).padStart(2, '0') }
function isoDate(d: Date) { return d.toISOString().slice(0, 10) }
function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r }
function jsDayToISO(jsDay: number) { return (jsDay + 6) % 7 } // 0=Sun → 6=Mon=0
function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}
function formatDateLabel(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
}
function ruleTime(r: RecurringRule) {
  return `${pad(r.startH)}:${pad(r.startM)} → ${pad(r.endH)}:${pad(r.endM)}`
}

export default function DisponibilitesPage() {
  const [slots, setSlots] = useState<Slot[]>([])
  const [recurring, setRecurring] = useState<RecurringRule[]>([])
  const [loading, setLoading] = useState(true)
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
    d.setHours(0, 0, 0, 0)
    return d
  })

  // Formulaire
  const [adding, setAdding] = useState(false)
  const [tab, setTab] = useState<'once' | 'recurring'>('once')

  // Ponctuel
  const [form, setForm] = useState({ date: isoDate(new Date()), startH: '09', startM: '00', endH: '17', endM: '00' })
  const [saving, setSaving] = useState(false)

  // Récurrent
  const [rDays, setRDays] = useState<boolean[]>([false, false, false, false, false, false, false])
  const [rForm, setRForm] = useState({ startH: '09', startM: '00', endH: '17', endM: '00', label: '' })
  const [rSaving, setRSaving] = useState(false)
  const [rError, setRError] = useState('')

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        apiFetch<Slot[]>('/providers/me/availability'),
        apiFetch<RecurringRule[]>('/providers/me/availability/recurring'),
      ])
      setSlots(s)
      setRecurring(r)
    } catch { /* silencieux */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const days = Array.from({ length: 14 }, (_, i) => addDays(weekStart, i))
  const week1 = days.slice(0, 7)
  const week2 = days.slice(7, 14)

  function slotsForDay(day: Date) {
    const d = isoDate(day)
    return slots.filter((s) => s.startAt.slice(0, 10) === d)
  }

  function recurringForDay(day: Date) {
    const iso = jsDayToISO(day.getDay())
    return recurring.filter((r) => r.dayOfWeek === iso && r.isActive)
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const startAt = `${form.date}T${form.startH}:${form.startM}:00`
      const endAt = `${form.date}T${form.endH}:${form.endM}:00`
      await apiFetch('/providers/me/availability', { method: 'POST', body: JSON.stringify({ startAt, endAt }) })
      setAdding(false)
      await load()
    } catch { /* silencieux */ }
    finally { setSaving(false) }
  }

  async function handleDelete(id: string) {
    await apiFetch(`/providers/me/availability/${id}`, { method: 'DELETE' })
    setSlots((prev) => prev.filter((s) => s.id !== id))
  }

  async function handleAddRecurring(e: React.FormEvent) {
    e.preventDefault()
    setRError('')
    const selectedDays = rDays.map((v, i) => v ? i : -1).filter((i) => i >= 0)
    if (selectedDays.length === 0) { setRError('Sélectionnez au moins un jour'); return }
    setRSaving(true)
    try {
      await Promise.all(selectedDays.map((dayOfWeek) =>
        apiFetch('/providers/me/availability/recurring', {
          method: 'POST',
          body: JSON.stringify({
            dayOfWeek,
            startH: parseInt(rForm.startH),
            startM: parseInt(rForm.startM),
            endH: parseInt(rForm.endH),
            endM: parseInt(rForm.endM),
            label: rForm.label || null,
          }),
        })
      ))
      setRDays([false, false, false, false, false, false, false])
      setRForm({ startH: '09', startM: '00', endH: '17', endM: '00', label: '' })
      setAdding(false)
      await load()
    } catch { setRError('Erreur lors de l\'enregistrement') }
    finally { setRSaving(false) }
  }

  async function handleToggleRecurring(rule: RecurringRule) {
    const updated = await apiFetch<RecurringRule>(`/providers/me/availability/recurring/${rule.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive: !rule.isActive }),
    })
    setRecurring((prev) => prev.map((r) => r.id === rule.id ? updated : r))
  }

  async function handleDeleteRecurring(id: string) {
    await apiFetch(`/providers/me/availability/recurring/${id}`, { method: 'DELETE' })
    setRecurring((prev) => prev.filter((r) => r.id !== id))
  }

  const weekLabel = `${days[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — ${days[13].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Mes disponibilités</h1>
          <p className="text-text-secondary text-sm mt-1">Déclarez vos plages horaires pour recevoir des missions.</p>
        </div>
        <button onClick={() => setAdding((v) => !v)} className="glass-btn-accent px-4 py-2.5 text-sm rounded-xl">
          {adding ? 'Annuler' : '+ Ajouter un créneau'}
        </button>
      </div>

      {/* Formulaire */}
      {adding && (
        <div className="glass-card p-5">
          {/* Tabs */}
          <div className="flex gap-1 mb-5 p-1 bg-white/[0.06] rounded-xl w-fit">
            {(['once', 'recurring'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={[
                  'px-4 py-1.5 rounded-lg text-sm transition-all',
                  tab === t ? 'bg-accent/20 border border-accent/30 text-accent' : 'text-text-secondary hover:text-text-primary',
                ].join(' ')}
              >
                {t === 'once' ? '📅 Ponctuel' : '🔁 Récurrent'}
              </button>
            ))}
          </div>

          {tab === 'once' ? (
            <form onSubmit={handleAdd} className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="glass-label">Date *</label>
                <input type="date" value={form.date} onChange={(e) => setForm((v) => ({ ...v, date: e.target.value }))} className="glass-input" required />
              </div>
              <div>
                <label className="glass-label">Début</label>
                <div className="flex gap-1 items-center">
                  <select value={form.startH} onChange={(e) => setForm((v) => ({ ...v, startH: e.target.value }))} className="glass-input w-20">
                    {HOURS.map((h) => <option key={h} value={pad(h)}>{pad(h)}h</option>)}
                  </select>
                  <span className="text-text-muted text-sm">:</span>
                  <select value={form.startM} onChange={(e) => setForm((v) => ({ ...v, startM: e.target.value }))} className="glass-input w-20">
                    {['00', '15', '30', '45'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="glass-label">Fin</label>
                <div className="flex gap-1 items-center">
                  <select value={form.endH} onChange={(e) => setForm((v) => ({ ...v, endH: e.target.value }))} className="glass-input w-20">
                    {HOURS.map((h) => <option key={h} value={pad(h)}>{pad(h)}h</option>)}
                  </select>
                  <span className="text-text-muted text-sm">:</span>
                  <select value={form.endM} onChange={(e) => setForm((v) => ({ ...v, endM: e.target.value }))} className="glass-input w-20">
                    {['00', '15', '30', '45'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" disabled={saving} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">
                {saving ? 'Ajout…' : 'Ajouter'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleAddRecurring} className="space-y-4">
              {/* Jours */}
              <div>
                <label className="glass-label mb-2">Jours de la semaine *</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_FR.map((d, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setRDays((prev) => { const n = [...prev]; n[i] = !n[i]; return n })}
                      className={[
                        'w-12 h-10 rounded-xl text-sm font-medium border transition-all',
                        rDays[i]
                          ? 'bg-accent/20 border-accent/40 text-accent'
                          : 'border-white/10 text-text-secondary hover:border-white/20 hover:text-text-primary',
                      ].join(' ')}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Horaires */}
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="glass-label">Début</label>
                  <div className="flex gap-1 items-center">
                    <select value={rForm.startH} onChange={(e) => setRForm((v) => ({ ...v, startH: e.target.value }))} className="glass-input w-20">
                      {HOURS.map((h) => <option key={h} value={pad(h)}>{pad(h)}h</option>)}
                    </select>
                    <span className="text-text-muted text-sm">:</span>
                    <select value={rForm.startM} onChange={(e) => setRForm((v) => ({ ...v, startM: e.target.value }))} className="glass-input w-20">
                      {['00', '15', '30', '45'].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="glass-label">Fin</label>
                  <div className="flex gap-1 items-center">
                    <select value={rForm.endH} onChange={(e) => setRForm((v) => ({ ...v, endH: e.target.value }))} className="glass-input w-20">
                      {HOURS.map((h) => <option key={h} value={pad(h)}>{pad(h)}h</option>)}
                    </select>
                    <span className="text-text-muted text-sm">:</span>
                    <select value={rForm.endM} onChange={(e) => setRForm((v) => ({ ...v, endM: e.target.value }))} className="glass-input w-20">
                      {['00', '15', '30', '45'].map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="glass-label">Libellé (optionnel)</label>
                  <input
                    type="text"
                    value={rForm.label}
                    onChange={(e) => setRForm((v) => ({ ...v, label: e.target.value }))}
                    placeholder="ex. Journée type"
                    className="glass-input"
                  />
                </div>
              </div>

              {rError && <p className="text-danger text-sm">{rError}</p>}

              <button type="submit" disabled={rSaving} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">
                {rSaving ? 'Enregistrement…' : '🔁 Enregistrer la récurrence'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Vue semaine */}
      <div className="glass-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
          <button onClick={() => setWeekStart((d) => addDays(d, -14))} className="glass-btn px-3 py-1.5 text-sm rounded-xl">‹ Préc.</button>
          <span className="text-text-primary text-sm font-medium">{weekLabel}</span>
          <button onClick={() => setWeekStart((d) => addDays(d, 14))} className="glass-btn px-3 py-1.5 text-sm rounded-xl">Suiv. ›</button>
        </div>

        {/* Légende */}
        <div className="flex items-center gap-4 px-5 py-2 border-b border-white/[0.06] bg-white/[0.02]">
          <span className="flex items-center gap-1.5 text-xs text-text-muted">
            <span className="w-3 h-3 rounded bg-success/40 border border-success/50" />Ponctuel
          </span>
          <span className="flex items-center gap-1.5 text-xs text-text-muted">
            <span className="w-3 h-3 rounded bg-accent/30 border border-accent/40" />Récurrent
          </span>
        </div>

        {loading ? (
          <div className="p-10 text-center text-text-muted animate-pulse text-sm">Chargement…</div>
        ) : (
          <>
            {[week1, week2].map((week, wi) => (
              <div key={wi}>
                {wi === 1 && <div className="border-t border-white/[0.10] bg-white/[0.02] px-5 py-1.5">
                  <span className="text-[10px] text-text-muted uppercase tracking-widest font-semibold">Semaine 2</span>
                </div>}
                <div className="grid grid-cols-7 divide-x divide-white/[0.06]">
                  {week.map((day) => {
                    const daySlots = slotsForDay(day)
                    const dayRecurring = recurringForDay(day)
                    const isToday = isoDate(day) === isoDate(new Date())
                    return (
                      <div key={isoDate(day)} className="min-h-[120px]">
                        <div className={`px-2 py-2 text-center border-b border-white/[0.06] ${isToday ? 'bg-accent/10' : ''}`}>
                          <p className={`text-xs font-semibold uppercase tracking-wider ${isToday ? 'text-accent' : 'text-text-muted'}`}>
                            {DAYS_FR[jsDayToISO(day.getDay())]}
                          </p>
                          <p className={`text-base font-bold ${isToday ? 'text-accent' : 'text-text-secondary'}`}>
                            {day.getDate()}
                          </p>
                        </div>
                        <div className="p-1.5 space-y-1">
                          {dayRecurring.map((r) => (
                            <div key={`r-${r.id}`} className="rounded-lg bg-accent/10 border border-accent/25 px-2 py-1.5">
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] text-accent/60">🔁</span>
                                <p className="text-accent text-[11px] font-medium">{pad(r.startH)}:{pad(r.startM)}</p>
                              </div>
                              <p className="text-accent/70 text-[10px]">{pad(r.endH)}:{pad(r.endM)}</p>
                            </div>
                          ))}
                          {daySlots.map((s) => (
                            <div key={s.id} className="group relative rounded-lg bg-success/15 border border-success/30 px-2 py-1.5">
                              <p className="text-success text-[11px] font-medium">{formatTime(s.startAt)}</p>
                              <p className="text-success/70 text-[10px]">{formatTime(s.endAt)}</p>
                              <button
                                onClick={() => handleDelete(s.id)}
                                className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 w-4 h-4 rounded flex items-center justify-center hover:bg-danger/20 text-danger text-[10px] transition-all"
                              >✕</button>
                            </div>
                          ))}
                          {daySlots.length === 0 && dayRecurring.length === 0 && (
                            <p className="text-text-muted text-[10px] text-center mt-3 opacity-50">—</p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Règles récurrentes */}
      {!loading && recurring.length > 0 && (
        <div className="glass-card p-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">
            🔁 Créneaux récurrents ({recurring.length})
          </h2>
          <div className="space-y-2">
            {recurring.map((r) => (
              <div key={r.id} className={[
                'flex items-center gap-3 px-4 py-3 rounded-xl border transition-all',
                r.isActive
                  ? 'bg-accent/5 border-accent/20'
                  : 'bg-white/[0.02] border-white/[0.06] opacity-50',
              ].join(' ')}>
                <div className="w-9 h-9 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent text-xs font-bold">{DAYS_FR[r.dayOfWeek]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary text-sm font-medium">
                    {DAYS_FULL[r.dayOfWeek]}
                    {r.label && <span className="ml-2 text-text-muted text-xs">· {r.label}</span>}
                  </p>
                  <p className="text-text-muted text-xs">{ruleTime(r)}</p>
                </div>
                <button
                  onClick={() => handleToggleRecurring(r)}
                  title={r.isActive ? 'Désactiver' : 'Activer'}
                  className={[
                    'p-2 rounded-lg transition-all text-sm',
                    r.isActive ? 'hover:bg-white/[0.08] text-success' : 'hover:bg-white/[0.08] text-text-muted',
                  ].join(' ')}
                >
                  {r.isActive ? '✓' : '○'}
                </button>
                <button
                  onClick={() => handleDeleteRecurring(r.id)}
                  className="p-2 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Créneaux ponctuels */}
      {!loading && slots.length > 0 && (
        <div className="glass-card p-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">
            📅 Créneaux ponctuels ({slots.length})
          </h2>
          <div className="space-y-2">
            {slots.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.07] group">
                <div className="w-8 h-8 rounded-xl bg-success/10 border border-success/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-success text-sm">✓</span>
                </div>
                <div className="flex-1">
                  <p className="text-text-primary text-sm font-medium">{formatDateLabel(s.startAt)}</p>
                  <p className="text-text-muted text-xs">{formatTime(s.startAt)} → {formatTime(s.endAt)}</p>
                </div>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && slots.length === 0 && recurring.length === 0 && (
        <div className="glass-card p-12 text-center">
          <p className="text-4xl mb-4">📅</p>
          <p className="text-text-primary font-medium mb-1">Aucun créneau déclaré</p>
          <p className="text-text-secondary text-sm">Ajoutez des plages ponctuelles ou des récurrences hebdomadaires.</p>
        </div>
      )}
    </div>
  )
}
